import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Notifications as NotificationsIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  Assignment as AssignmentIcon,
  AccountTree as HierarchyIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../shared/contexts/AuthContext';

const drawerWidth = 240; // Reduced from 280

const AdminLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    navigate('/login');
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard'
    },
    {
      text: 'User Management',
      icon: <PeopleIcon />,
      path: '/users'
    },
    {
      text: 'Role Management',
      icon: <SecurityIcon />,
      path: '/roles'
    },
    {
      text: 'Role Permission Setup',
      icon: <AssignmentIcon />,
      path: '/permission-setup'
    },
    {
      text: 'Permission Management',
      icon: <SecurityIcon />,
      path: '/permission-management'
    },
    {
      text: 'Portal Configuration',
      icon: <AdminIcon />,
      path: '/portals'
    }
  ];

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: '#ffffff' }}>
      <Toolbar
        sx={{
          bgcolor: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
          background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
          color: 'white',
          py: 2, // Reduced from 3
          minHeight: '56px', // More compact
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        {/* <img src="/hitpa_logo_transparent.png" alt="Logo" style={{ maxHeight: 40 }} /> */}
        <AdminIcon sx={{ mr: 2, fontSize: 24 }} /> {/* Reduced icon size */}
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            fontWeight: 600, // Reduced from 700
            fontSize: '1.125rem', // Reduced from 1.25rem
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          Admin Console
        </Typography>
      </Toolbar>

      <List sx={{ px: 2, py: 2 }}> {/* Reduced padding */}
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}> {/* Reduced margin */}
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 1.5, // Reduced from 2
                py: 1, // Reduced from 1.5
                px: 1.5, // Reduced from 2
                '&.Mui-selected': {
                  backgroundColor: '#eff6ff',
                  borderLeft: '4px solid #2563eb',
                  '& .MuiListItemIcon-root': {
                    color: '#2563eb',
                  },
                  '& .MuiListItemText-primary': {
                    color: '#1e293b',
                    fontWeight: 600,
                  },
                },
                '&:hover': {
                  backgroundColor: '#f8fafc',
                  '& .MuiListItemIcon-root': {
                    color: '#2563eb',
                  },
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}> {/* Reduced from 40 */}
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                slotProps={{
                  primary: {
                    sx: {
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.8125rem' // Reduced from 0.875rem
                    }
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#ffffff',
          color: '#1e293b',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
        }}
      >
        <Toolbar sx={{ py: 0.5, minHeight: '56px' }}> {/* More compact toolbar */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontWeight: 600,
              color: '#1e293b'
            }}
          >
            HITPA Admin Console
          </Typography>

          <IconButton
            sx={{
              mr: 2,
              color: '#64748b',
              '&:hover': {
                bgcolor: '#f1f5f9'
              }
            }}
          >
            <Badge badgeContent={3} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            sx={{
              color: '#64748b',
              '&:hover': {
                bgcolor: '#f1f5f9'
              }
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#2563eb',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: 600,
                fontSize: '0.875rem'
              }}
            >
              {user?.firstName?.charAt(0) || user?.username?.charAt(0) || 'A'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem onClick={handleProfileMenuClose}>
          <AccountCircleIcon sx={{ mr: 2 }} />
          Profile
        </MenuItem>
        <MenuItem onClick={handleProfileMenuClose}>
          <SettingsIcon sx={{ mr: 2 }} />
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 2 }} />
          Logout
        </MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="admin navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 7, // Reduced from 8
          bgcolor: '#f8fafc',
          minHeight: 'calc(100vh - 56px)' // Adjusted for compact header
        }}
      >
        <Box sx={{ p: 3 }}> {/* Reduced from 4 */}
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
